import React, { useState, useEffect, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calendar.css';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Calendar as CalendarIcon, Clock, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';

const localizer = momentLocalizer(moment);

// Color palette for different tasks (matching TutorDashboard)
const TASK_COLORS = [
  '#0891b2', // cyan
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#f59e0b', // amber
  '#10b981', // emerald
  '#ef4444', // red
  '#3b82f6', // blue
];

const ClassScheduleViewer = ({ role = 'student' }) => {
  const { getAccessToken } = useAuth();
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date()); // Add date state for navigation
  const [events, setEvents] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [schedules, setSchedules] = useState([]); // Store raw schedules separately

  // Get consistent color for a task (matching TutorDashboard algorithm) - memoized
  const getTaskColor = useCallback((taskId) => {
    if (!taskId) return '#6b7280'; // gray for no task
    // Generate consistent color based on task ID using hash function
    let hash = 0;
    for (let i = 0; i < taskId.length; i++) {
      hash = taskId.charCodeAt(i) + ((hash << 5) - hash);
    }
    return TASK_COLORS[Math.abs(hash) % TASK_COLORS.length];
  }, []);

  // Style getter for calendar events
  const eventStyleGetter = (event) => {
    const backgroundColor = event.resource?.taskColor || '#0891b2';
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block'
      }
    };
  };

  // Fetch tasks - stable, only runs once
  const fetchTasks = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        setTasks([]);
        return;
      }

      // Students and parents should use /api/tasks, tutors use /api/schedules/tasks
      const endpoint = role === 'student' || role === 'parent' 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/tasks`
        : `${process.env.REACT_APP_BACKEND_URL}/api/schedules/tasks`;

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const fetchedTasks = await response.json();
        setTasks(fetchedTasks);
      } else {
        console.warn('Failed to fetch tasks, will show schedules without task names');
        setTasks([]);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
    }
  }, [getAccessToken, role]);

  // Fetch schedules - stable, only depends on getAccessToken
  const fetchSchedules = useCallback(async () => {
    try {
      const token = getAccessToken();
      if (!token) {
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/schedules`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const fetchedSchedules = await response.json();
        setSchedules(fetchedSchedules); // Store raw schedules
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  }, [getAccessToken]);

  // Convert schedules to calendar events - runs when schedules or tasks change
  useEffect(() => {
    // Only convert to events if we're not in initial loading state
    if (loading) return;
    
    const calendarEvents = schedules.map(schedule => {
      // Parse the UTC date and time
      const [year, month, day] = schedule.scheduled_date.split('-').map(Number);
      const [hours, minutes] = schedule.scheduled_time.split(':').map(Number);
      
      // Create Date object in UTC
      const startDate = new Date(Date.UTC(year, month - 1, day, hours, minutes));
      const endDate = new Date(startDate.getTime() + schedule.duration_minutes * 60000);
      
      // Find the task for this schedule
      const task = tasks.find(t => t.id === schedule.task_id);
      const taskTitle = task ? `${task.title} - ${task.subject}` : 'Tutoring Session';
      
      return {
        id: schedule.id,
        title: taskTitle,
        start: startDate,
        end: endDate,
        resource: {
          ...schedule,
          taskTitle: task?.title || 'N/A',
          taskSubject: task?.subject || 'N/A',
          taskColor: getTaskColor(schedule.task_id) // Pre-calculate color
        }
      };
    });
    
    setEvents(calendarEvents);
  }, [schedules, tasks, loading, getTaskColor]);

  // Initial data fetch - runs once on mount
  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      if (!mounted) return;
      
      setLoading(true);
      // Fetch both tasks and schedules in parallel
      await Promise.all([
        fetchTasks(),
        fetchSchedules()
      ]);
      
      if (mounted) {
        setLoading(false);
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [fetchTasks, fetchSchedules]);

  // Set up real-time subscription - runs once
  useEffect(() => {
    const subscription = supabase
      .channel('class_schedules_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'class_schedules'
        },
        (payload) => {
          console.log('Schedule change detected:', payload);
          // Only refetch schedules, don't change loading state
          fetchSchedules();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchSchedules]); // Add fetchSchedules to deps since it's a stable callback

  // Handle event selection (clicking on existing event)
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
  }, []);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-cyan-600" />
          {role === 'student' ? 'My Class Schedule' : "Children's Class Schedule"}
        </h2>
        <div className="flex gap-2">
          <Button
            onClick={() => setView('month')}
            variant={view === 'month' ? 'default' : 'outline'}
            size="sm"
          >
            Month
          </Button>
          <Button
            onClick={() => setView('week')}
            variant={view === 'week' ? 'default' : 'outline'}
            size="sm"
          >
            Week
          </Button>
          <Button
            onClick={() => setView('day')}
            variant={view === 'day' ? 'default' : 'outline'}
            size="sm"
          >
            Day
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading schedule...</p>
          </div>
        </div>
      ) : events.length === 0 ? (
        <div className="flex items-center justify-center h-[600px]">
          <div className="text-center">
            <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No classes scheduled yet</p>
          </div>
        </div>
      ) : (
        <div style={{ height: '600px' }}>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={(newDate) => setDate(newDate)}
            onSelectEvent={handleSelectEvent}
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
          />
        </div>
      )}

      {/* View Schedule Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Class Details</DialogTitle>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-4">
              <div className="bg-cyan-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarIcon className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                  <span className="font-semibold text-gray-900 dark:text-white">{selectedEvent.title}</span>
                </div>
                {selectedEvent.resource.taskTitle && selectedEvent.resource.taskTitle !== 'N/A' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Task: {selectedEvent.resource.taskTitle}
                  </p>
                )}
                {selectedEvent.resource.taskSubject && selectedEvent.resource.taskSubject !== 'N/A' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Subject: {selectedEvent.resource.taskSubject}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Date</Label>
                  <p className="text-sm font-medium mt-1 text-gray-900 dark:text-white">
                    {moment(selectedEvent.start).format('MMMM DD, YYYY')}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Time</Label>
                  <p className="text-sm font-medium mt-1 text-gray-900 dark:text-white">
                    {moment(selectedEvent.start).format('hh:mm A')}
                  </p>
                </div>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-400">Duration</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {selectedEvent.resource.duration_minutes} minutes
                  </p>
                </div>
              </div>

              {selectedEvent.resource.recurrence !== 'none' && (
                <div className="bg-purple-50 dark:bg-gray-700 rounded-lg p-3">
                  <Label className="text-gray-600 dark:text-gray-400">Recurring Class</Label>
                  <p className="text-sm font-medium mt-1 text-gray-900 dark:text-white capitalize">
                    {selectedEvent.resource.recurrence}
                    {selectedEvent.resource.recurrence_end_date && 
                      ` until ${moment(selectedEvent.resource.recurrence_end_date).format('MMM DD, YYYY')}`
                    }
                  </p>
                </div>
              )}

              {selectedEvent.resource.meeting_link && (
                <div className="bg-green-50 dark:bg-gray-700 rounded-lg p-3">
                  <Label className="text-gray-600 dark:text-gray-400">Meeting Link</Label>
                  <p className="text-sm font-medium mt-1 text-blue-600 dark:text-blue-400 break-all">
                    {selectedEvent.resource.meeting_link}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button
                  variant="outline"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </Button>
                {selectedEvent.resource.meeting_link && (
                  <Button
                    className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={() => {
                      window.open(selectedEvent.resource.meeting_link, '_blank');
                    }}
                  >
                    Join Class
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassScheduleViewer;
